import React, { useMemo, useState } from "react";

import FilterListIcon from "@mui/icons-material/FilterList";
import { useParams } from "react-router-dom";

import useDeleteRelationshipNote from "@fartherfinance/frontend/api/Notes/hooks/useDeleteRelationshipNote";
import useGetRelationshipNotes from "@fartherfinance/frontend/api/Notes/hooks/useGetRelationshipNotes";
import usePostRelationshipNote from "@fartherfinance/frontend/api/Notes/hooks/usePostRelationshipNote";
import { RelationshipNoteId } from "@fartherfinance/frontend/api/Notes/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import AnimationContainer from "@src/sharedComponents/AnimationContainer/AnimationContainer";
import Conditional from "@src/sharedComponents/Conditional/Conditional";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import PaginatedTable from "@src/sharedComponents/Table/PaginatedTable";
import { TableSortOrder } from "@src/sharedComponents/Table/Types";

import DateRangeFilter, {
  DateRangeOption,
  FiltersAppliedSchema,
} from "./DateRangeFilter";
import DateRangeFilterChip from "./DateRangeFilterChip";
import DeleteNoteModal from "./DeleteNoteModal";
import { NotesTableColumns, useNotesColumns, useNotesRows } from "./helpers";
import LoadingState from "./LoadingState";
import NoteForm from "./NoteForm";
import NotesHeader from "./NotesHeader";
import { EmptyNotesState, ErrorNotesState } from "./states/EmptyNotesState";

import styles from "./ClientNotes.module.css";

const PAGE_LIMIT = 10;

const ClientNotes = (): JSX.Element => {
  const { clientId } = useParams<{ clientId: ClientId }>();

  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<TableSortOrder>("desc");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<
    RelationshipNoteId | undefined
  >(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<
    RelationshipNoteId | undefined
  >(undefined);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState<FiltersAppliedSchema>({});
  const [dateRangeOption, setDateRangeOption] =
    useState<DateRangeOption>("All");

  const showStatusNotification = useStatusNotification();

  const advisorAuth = useAdvisorRequestAuth();

  const {
    data: notesData,
    hasError,
    isLoading,
  } = useGetRelationshipNotes(
    advisorAuth?.advisorId
      ? {
          advisorId: advisorAuth?.advisorId,
          clientId,
          limit: PAGE_LIMIT,
          page: currentPage,
          sortOrder,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
        }
      : null,
    advisorAuth
  );

  const createNote = usePostRelationshipNote(advisorAuth);
  const deleteNote = useDeleteRelationshipNote(advisorAuth);

  const handleCreateNote = () => {
    setEditingNoteId(undefined);
    setShowCreateForm(true);
  };

  const handleNoteClick = (noteId: RelationshipNoteId) => {
    setEditingNoteId(noteId);
    setShowCreateForm(true);
  };

  const handleSaveNote = async (content: string) => {
    if (!advisorAuth?.advisorId || !clientId) {
      showStatusNotification(
        "Failed to create note: Missing advisor ID",
        "Error"
      );
      throw new Error("Missing advisor ID or client ID");
    }

    await createNote({
      advisorId: advisorAuth.advisorId,
      clientId,
      body: {
        note: content,
      },
    });
    setShowCreateForm(false);
    showStatusNotification("Note created successfully", "Success");
  };

  const handleCancelForm = () => {
    setShowCreateForm(false);
    setEditingNoteId(undefined);
  };

  const handleDeleteNote = (noteId: RelationshipNoteId) => {
    setDeletingNoteId(noteId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (deletingNoteId && advisorAuth?.advisorId) {
      await deleteNote({
        advisorId: advisorAuth.advisorId,
        noteId: deletingNoteId,
      });

      showStatusNotification("Note deleted successfully", "Success");
      setShowDeleteModal(false);
      setShowCreateForm(false);
      setEditingNoteId(undefined);
      setDeletingNoteId(undefined);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeletingNoteId(undefined);
  };

  const handleUnsavedChangesWarning = () => {
    setShowUnsavedChangesModal(true);
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesModal(false);
    setShowCreateForm(false);
    setEditingNoteId(undefined);
  };

  const handleKeepEditing = () => {
    setShowUnsavedChangesModal(false);
  };

  const handleFilterClick = () => {
    setShowFilterModal(true);
  };

  const handleApplyFilters = (newFilters: FiltersAppliedSchema) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleClearDateFilters = () => {
    setFilters({});
    setDateRangeOption("All");
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(filters.dateFrom || filters.dateTo);

  const notes = notesData?.data || [];

  const columns = useNotesColumns({
    sortOrder,
    sortable: notes.length > 0,
  });

  const tableRows = useNotesRows(notes, handleNoteClick);

  const handleColumnSelect = (
    _column: NotesTableColumns,
    newSortOrder: TableSortOrder
  ) => {
    setSortOrder(newSortOrder);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterClose = () => {
    setShowFilterModal(false);
  };

  const paginationConfig = useMemo(
    () => ({
      currentPage,
      pageSize: PAGE_LIMIT,
      totalCount: notesData?.pagination.totalCount ?? 0,
      prevPage: currentPage > 1 ? currentPage - 1 : undefined,
      nextPage: notesData?.pagination.hasNext ? currentPage + 1 : undefined,
      totalPages: notesData?.pagination.totalPages ?? 0,
    }),
    [currentPage, notesData?.pagination]
  );

  return (
    <>
      <AnimationContainer
        contentKey={showCreateForm ? "create" : "list"}
        style={{ height: "100%" }}
      >
        {showCreateForm ? (
          <NoteForm
            noteId={editingNoteId}
            onSave={handleSaveNote}
            onCancel={handleCancelForm}
            onDelete={handleDeleteNote}
            onUnsavedChangesWarning={handleUnsavedChangesWarning}
            deletingNoteId={Boolean(deletingNoteId)}
          />
        ) : (
          <div className={styles.container}>
            <NotesHeader
              notesCount={notesData?.pagination.totalCount ?? 0}
              onFilterClick={handleFilterClick}
              onCreateNote={handleCreateNote}
              filterChip={
                dateRangeOption === "Custom" && hasActiveFilters ? (
                  <DateRangeFilterChip
                    filters={filters}
                    onClear={handleClearDateFilters}
                    onPress={handleFilterClick}
                  />
                ) : null
              }
              filterComponent={
                <DateRangeFilter
                  isOpen={showFilterModal}
                  onClose={handleFilterClose}
                  onApply={handleApplyFilters}
                  currentFilters={filters}
                  dateRangeOption={dateRangeOption}
                  onDateRangeOptionChange={setDateRangeOption}
                  triggerElement={
                    <IconButton
                      IconComponent={FilterListIcon}
                      size="medium"
                      onClick={handleFilterClick}
                    />
                  }
                />
              }
            />

            <Conditional condition={isLoading}>
              <LoadingState />
            </Conditional>

            <Conditional condition={hasError}>
              <ErrorNotesState />
            </Conditional>
            <Conditional
              condition={notes.length === 0 && !isLoading && !hasError}
            >
              <EmptyNotesState onNewNoteClick={handleCreateNote} />
            </Conditional>

            {/* Notes Table */}
            <Conditional
              condition={notes.length !== 0 && !isLoading && !hasError}
            >
              <PaginatedTable
                columns={columns}
                rows={tableRows}
                onPageChange={handlePageChange}
                onColumnSelect={handleColumnSelect}
                showEmptyRows={false}
                pagination={paginationConfig}
              />
            </Conditional>
          </div>
        )}
      </AnimationContainer>

      {/* Delete Modal */}
      <DeleteNoteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete this note?"
        description="Once it's deleted it cannot be recovered."
        cancelText="Cancel"
        confirmText="Yes, delete this note"
      />

      {/* Unsaved Changes Modal */}
      <DeleteNoteModal
        isOpen={showUnsavedChangesModal}
        onClose={handleKeepEditing}
        onConfirm={handleDiscardChanges}
        title="Discard unsaved changes?"
        description="You have unsaved changes that will be lost if you continue."
        cancelText="Keep editing"
        confirmText="Discard changes"
      />
    </>
  );
};

export default ClientNotes;
